import { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Input,
    Space,
    Table,
    TablePaginationConfig,
    Tooltip,
    Tag,
    Modal,
} from "antd";
import {
    EditOutlined,
    FilterOutlined,
    OrderedListOutlined,
    SearchOutlined,
    CloseOutlined,
    DeleteOutlined,
    CheckOutlined,
    RetweetOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../../components/page-header/page-header";
import CustomDrawer from "../../../../components/drawer";
import { GetKPICycles, DeleteKPICycle } from "../../../../utils/api.constant";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { TKPICycle, TFilterModel } from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { defaultFilterParams } from "../../../../utils/constants";
import KPICycleForm from "./form";
import dayjs from "dayjs";

export default function KPICycleList() {
    const { post } = useFetch();
    const { onShowToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<TKPICycle[]>([]);
    const [searchText, setSearchText] = useState("");
    const [filterParams, setFilterParams] = useState<TFilterModel>(defaultFilterParams);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const requestPayload = {
                data: {
                    currentPage: 1,
                    pageSize: -1,
                },
            };

            const response = await post(GetKPICycles, requestPayload);
            if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
                const formattedData = response.data.map((item: TKPICycle) => ({
                    ...item,
                    key: (item.id ?? "").toString(),
                }));
                setData(formattedData);
                setFilterParams({ ...response.filterModel });
            } else {
                onShowToast({
                    type: ToastType.error,
                    title: <CloseOutlined />,
                    position: ToastOpen.centerTop,
                    content: response.dataResponse.description,
                });
            }
        } catch (error) {
            console.error("Error fetching KPI cycles:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (record: TKPICycle) => {
        Modal.confirm({
            title: "Confirm Delete",
            content: `Are you sure you want to delete the cycle "${record.name}"?`,
            okText: "Yes",
            cancelText: "No",
            onOk: async () => {
                try {
                    setIsLoading(true);
                    const payload = {
                        data: {
                            id: record.id,
                        },
                    };
                    const response = await post(DeleteKPICycle, payload);
                    if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
                        onShowToast({
                            type: ToastType.success,
                            title: <CheckOutlined />,
                            position: ToastOpen.centerTop,
                            content: response.dataResponse.description,
                        });
                        fetchData();
                    } else {
                        onShowToast({
                            type: ToastType.error,
                            title: <CloseOutlined />,
                            position: ToastOpen.centerTop,
                            content: response.dataResponse.description,
                        });
                    }
                } catch (error) {
                    console.error("Error deleting KPI cycle:", error);
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    const columns = [
        {
            title: "Cycle Name",
            dataIndex: "name",
            key: "name",
            sorter: (a: TKPICycle, b: TKPICycle) => a.name.localeCompare(b.name),
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            key: "startDate",
            render: (date: string) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
            sorter: (a: TKPICycle, b: TKPICycle) => dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            render: (date: string) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
            sorter: (a: TKPICycle, b: TKPICycle) => dayjs(a.endDate).unix() - dayjs(b.endDate).unix(),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: number) => (
                <Tag color={isActive === 1 ? "success" : "error"}>
                    {isActive === 1 ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Action",
            key: "actions",
            width: "80px",
            render: (item: TKPICycle) => (
                <Dropdown
                    trigger={["click"]}
                    menu={{
                        items: [
                            {
                                key: "edit",
                                label: (
                                    <CustomDrawer
                                        size="default"
                                        trigger={
                                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                                <EditOutlined style={{ fontSize: 12 }} />
                                                Edit
                                            </div>
                                        }
                                        component={KPICycleForm}
                                        componentProps={{
                                            id: item.id,
                                            onRefreshList: fetchData,
                                        }}
                                        title="Edit Performance Review Cycle"
                                    />
                                ),
                            },
                            {
                                key: "delete",
                                danger: true,
                                label: (
                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                        <DeleteOutlined style={{ fontSize: 12 }} />
                                        Delete
                                    </div>
                                ),
                                onClick: () => handleDelete(item),
                            },
                        ],
                    }}
                >
                    <div style={{ textAlign: "center", cursor: "pointer" }}>
                        <OrderedListOutlined />
                    </div>
                </Dropdown>
            ),
        },
    ];

    const filteredData = data.filter((item) => {
        const search = searchText.toLowerCase();
        return (
            item.name?.toLowerCase().includes(search) ||
            item.description?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="pageBody">
            <PageHeader
                icon={<RetweetOutlined />}
                title={`${filterParams?.totalRows || 0} Total Performance Review Cycles`}
                size="large"
            >
                <Space>
                    <CustomDrawer
                        size="default"
                        trigger={
                            <Button disabled={isLoading} type="primary">
                                Add Cycle
                            </Button>
                        }
                        component={KPICycleForm}
                        componentProps={{
                            id: 0,
                            onRefreshList: fetchData,
                        }}
                        title="Add Performance Review Cycle"
                    />
                    <Input
                        placeholder="Search cycles..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        prefix={<SearchOutlined style={{ color: "var(--bs-search-style)" }} />}
                    />
                    <Tooltip title="Reset Filters">
                        <FilterOutlined onClick={() => setSearchText("")} />
                    </Tooltip>
                </Space>
            </PageHeader>

            <div className="tableWrap">
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={isLoading}
                    pagination={{
                        position: ["bottomLeft"],
                        showSizeChanger: true,
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
                    }}
                    size="small"
                    bordered={false}
                />
            </div>
        </div>
    );
}
